import React from 'react';
import { X } from 'lucide-react';
import { safeTagColor } from './tagPalette';
import './Tags.css';

/**
 * One tag pill (WR-010).
 *
 * Colour comes entirely from the palette class — `tag-chip-<id>` resolves to
 * a CSS-variable pair declared per theme in Tags.css — so this renders
 * legibly in all five themes without knowing a single colour value.
 */
function TagChip({ tag, onRemove, className = '', title }) {
  if (!tag) return null;
  const color = safeTagColor(tag.color);

  return (
    <span
      className={`tag-chip tag-chip-${color} ${className}`.trim()}
      title={title || tag.name}
    >
      {tag.name}
      {onRemove && (
        <button
          type="button"
          className="tag-chip-remove"
          onClick={(e) => { e.stopPropagation(); onRemove(tag); }}
          aria-label={`Remove tag ${tag.name}`}
        >
          <X size={11} />
        </button>
      )}
    </span>
  );
}

/**
 * A row of chips with a hard cap and a "+N" marker.
 *
 * Cards use this with max=3: a task can hold any number of tags, but a card
 * that grows with them wrecks the column rhythm, so the card shows three and
 * counts the rest.
 */
export function TagChipRow({ tags, max = null, className = '' }) {
  if (!tags?.length) return null;
  const shown = max ? tags.slice(0, max) : tags;
  const hidden = tags.length - shown.length;

  return (
    <div className={`tag-chip-row ${className}`.trim()}>
      {shown.map(tag => <TagChip key={tag._id} tag={tag} />)}
      {hidden > 0 && (
        <span
          className="tag-chip tag-chip-more"
          title={tags.slice(shown.length).map(t => t.name).join(', ')}
        >
          +{hidden}
        </span>
      )}
    </div>
  );
}

export default TagChip;
